import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { CycleBudgetFieldOrdinal, AmountCode, AmountColor, PercentageCalculationRule,
    AmountPayType, EventTypes, TargetSource, MainViewPriority, StockDisplayType } from 'typings/enums';
import { ICycleBudgetField } from 'typings/cycleBudgetField';

export class CycleBudgetFieldFactory {

    getIncreaseBudgetType(): CycleBudgetField {
        return new CycleBudgetField({
            amountCycleId: -(CycleBudgetFieldOrdinal.Merit),
            amountCode: AmountCode.Merit,
            title: 'Increase',
            mainViewPriority: MainViewPriority.Merit,
            payType: AmountPayType.BasePay,
            eventType: EventTypes.UpdateAmountEvent,
            percentageCalculationRule: PercentageCalculationRule.PercentageOfCurrentPay,
            contentUnderneath: { percentageOfTarget: true, amount: false } as IContentUnderneath,
            targetSource: TargetSource.EmployeeData,
            hidePercentageIfTargetZero: false,
            showInDetailView: true,
            eligibilityRule: null,
            prorationRule: null,
            ordinal: CycleBudgetFieldOrdinal.Merit,
            color: AmountColor.Increase,
            colorPriority: 1,
            shouldShowOriginalBudget: true
        } as ICycleBudgetField, '');
    }

    getBonusBudgetType(): CycleBudgetField {
        return new CycleBudgetField({
            amountCycleId: -(CycleBudgetFieldOrdinal.Bonus),
            amountCode: AmountCode.Bonus,
            title: 'Bonus',
            mainViewPriority: MainViewPriority.Bonus,
            payType: AmountPayType.None,
            eventType: EventTypes.UpdateAmountEvent,
            percentageCalculationRule: PercentageCalculationRule.PercentageOfCurrentPay,
            contentUnderneath: { percentageOfTarget: true, amount: true } as IContentUnderneath,
            targetSource: TargetSource.EmployeeData,
            hidePercentageIfTargetZero: true,
            showInDetailView: true,
            eligibilityRule: null,
            prorationRule: null,
            ordinal: CycleBudgetFieldOrdinal.Bonus,
            color: AmountColor.Bonus,
            colorPriority: 2,
            shouldShowOriginalBudget: true
        } as ICycleBudgetField, '');
    }

    getMarketAdjustmentBudgetType(): CycleBudgetField {
        return new CycleBudgetField({
            amountCycleId: -(CycleBudgetFieldOrdinal.MarketAdjustment),
            amountCode: AmountCode.MarketAdjustment,
            title: 'Market Adj',
            mainViewPriority: MainViewPriority.MarketAdjustment,
            payType: AmountPayType.BasePay,
            eventType: EventTypes.UpdateAmountEvent,
            percentageCalculationRule: PercentageCalculationRule.PercentageOfCurrentPay,
            contentUnderneath: { percentageOfTarget: false, amount: false } as IContentUnderneath,
            hidePercentageIfTargetZero: false,
            showInDetailView: true,
            eligibilityRule: null,
            prorationRule: null,
            ordinal: CycleBudgetFieldOrdinal.MarketAdjustment,
            color: AmountColor.MarketAdjustment,
            colorPriority: 6,
            shouldShowOriginalBudget: false
        } as ICycleBudgetField, '');
    }

    getLumpSumBudgetType(): CycleBudgetField {
        return new CycleBudgetField({
            amountCycleId: -(CycleBudgetFieldOrdinal.LumpSum),
            amountCode: AmountCode.LumpSum,
            title: 'Lump Sum',
            mainViewPriority: MainViewPriority.LumpSum,
            payType: AmountPayType.None,
            eventType: EventTypes.UpdateAmountEvent,
            percentageCalculationRule: PercentageCalculationRule.PercentageOfCurrentPay,
            contentUnderneath: { percentageOfTarget: false, amount: false } as IContentUnderneath,
            hidePercentageIfTargetZero: false,
            showInDetailView: true,
            eligibilityRule: null,
            prorationRule: null,
            rangeEnforcement: {
                useAutoCalculateLumpSum: false,
                allowOverrideLumpSum: false,
                autoCalculatePercentage: 0} as IRangeEnforcement,
            ordinal: CycleBudgetFieldOrdinal.LumpSum,
            color: AmountColor.LumpSum,
            colorPriority: 5,
            shouldShowOriginalBudget: false
        } as ICycleBudgetField, '');
    }

    getPromotionBudgetType(): CycleBudgetField {
        return new CycleBudgetField({
            amountCycleId: -(CycleBudgetFieldOrdinal.Promotion),
            amountCode: AmountCode.Promotion,
            title: 'Promotion',
            mainViewPriority: null,
            payType: AmountPayType.BasePay,
            eventType: EventTypes.UpdateAmountEvent,
            percentageCalculationRule: PercentageCalculationRule.PercentageOfCurrentPay,
            contentUnderneath: { percentageOfTarget: false, amount: false } as IContentUnderneath,
            hidePercentageIfTargetZero: false,
            showInDetailView: false,
            eligibilityRule: null,
            prorationRule: null,
            ordinal: CycleBudgetFieldOrdinal.Promotion,
            color: AmountColor.Promotion,
            colorPriority: 3,
            shouldShowOriginalBudget: false
        } as ICycleBudgetField, '');
    }

    getStockBudgetType(): CycleBudgetField {
        return new CycleBudgetField({
            amountCycleId: -(CycleBudgetFieldOrdinal.Stock),
            amountCode: AmountCode.Stock,
            title: 'Stock',
            mainViewPriority: MainViewPriority.Stock,
            payType: AmountPayType.None,
            eventType: EventTypes.UpdateAmountEvent,
            contentUnderneath: { percentageOfTarget: true, amount: false } as IContentUnderneath,
            targetSource: TargetSource.SetBudget,
            eligibilityRule: null,
            prorationRule: null,
            showInDetailView: false,
            ordinal: CycleBudgetFieldOrdinal.Stock,
            color: AmountColor.Stock,
            colorPriority: 7,
            shouldShowOriginalBudget: true,
            stockDisplayType: StockDisplayType.Unit
        } as ICycleBudgetField, '');
    }

    getVariablePayBudgetType(): CycleBudgetField {
        return new CycleBudgetField({
            amountCycleId: -(CycleBudgetFieldOrdinal.VariablePay),
            amountCode: AmountCode.VariablePay,
            title: 'Variable Pay',
            mainViewPriority: MainViewPriority.VariablePay,
            payType: AmountPayType.None,
            eventType: EventTypes.UpdateAmountEvent,
            percentageCalculationRule: PercentageCalculationRule.PercentageOfCurrentPay,
            contentUnderneath: { percentageOfTarget: true, amount: true } as IContentUnderneath,
            targetSource: TargetSource.EmployeeData,
            hidePercentageIfTargetZero: true,
            showInDetailView: true,
            eligibilityRule: null,
            prorationRule: null,
            ordinal: CycleBudgetFieldOrdinal.VariablePay,
            color: AmountColor.VariablePay,
            colorPriority: 4,
            shouldShowOriginalBudget: true
        } as ICycleBudgetField, '');
    }

    getMetadata(amountCode: AmountCode): CycleBudgetField {
        switch (amountCode) {
            case AmountCode.Merit:
                return this.getIncreaseBudgetType();
            case AmountCode.Bonus:
                return this.getBonusBudgetType();
            case AmountCode.MarketAdjustment:
                return this.getMarketAdjustmentBudgetType();
            case AmountCode.LumpSum:
                return this.getLumpSumBudgetType();
            case AmountCode.Promotion:
                return this.getPromotionBudgetType();
            case AmountCode.Stock:
                return this.getStockBudgetType();
            case AmountCode.VariablePay:
                return this.getVariablePayBudgetType();
        }
    }

    getAllMetadata(): CycleBudgetField[] {
        return [this.getIncreaseBudgetType(), this.getBonusBudgetType(), this.getMarketAdjustmentBudgetType(),
            this.getLumpSumBudgetType(), this.getPromotionBudgetType(), this.getStockBudgetType(), this.getVariablePayBudgetType()];
    }

    getAllMetadataButStock(): CycleBudgetField[] {
        return [this.getIncreaseBudgetType(), this.getBonusBudgetType(), this.getMarketAdjustmentBudgetType(),
            this.getLumpSumBudgetType(), this.getPromotionBudgetType(), this.getVariablePayBudgetType()];
    }
}